<template>
  <StepCard
    step-id="create_teams"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('create_teams')"
    :save-hint
    @save="save"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <ZoneTable
        :columns="summaryColumns"
        :rows="data"
        :toolbar="false"
        :paginated="false"
        sticky-header
        :label="t('step_names.create_teams')"
        data-testid="teams-summary"
      />
    </template>

    <form
      class="flex flex-col gap-2"
      novalidate
      @submit="addName"
    >
      <div class="relative">
        <ZoneTextInput
          v-model="name"
          :label="t('team_name')"
          :placeholder="t('team_placeholder')"
          :invalid="!!errors.name"
          class="pr-10!"
          autocomplete="off"
          data-testid="team-name"
          @blur="validateTypedName"
        />
        <div class="absolute bottom-0 right-3 flex h-10 items-center">
          <ZoneButton
            reset
            :disabled="!name || !meta.valid"
            :tooltip="name && meta.valid ? null : t(name ? 'fix_errors' : 'add_hint')"
            :aria-label="t('add')"
            class="flex cursor-pointer items-center text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
            data-testid="team-add"
            @click="addName"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="size-4"
              aria-hidden="true"
            ><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>
          </ZoneButton>
        </div>
      </div>
      <p
        v-if="errors.name"
        class="text-xs text-red-600"
        role="alert"
        data-testid="team-error"
      >
        {{ errors.name }}
      </p>
      <p class="flex items-center gap-1.5 text-xs text-gray-500">
        {{ t('press') }}
        <kbd class="inline-flex items-center gap-1 rounded border font-mono border-gray-300 bg-gray-50 px-1.5 py-0.5 font-medium text-gray-600">{{ t('enter') }} <span aria-hidden="true">↵</span></kbd>
        {{ t('to_add') }}
      </p>
    </form>
    <div
      v-if="names.length > 0"
      class="mt-4 flex flex-wrap items-center gap-2"
      data-testid="team-chips"
    >
      <span
        v-for="teamName in names"
        :key="teamName"
        class="inline-flex items-center gap-1.5 rounded-full border border-primary-700 bg-primary-50 px-3 py-2 text-sm text-primary-700"
        :data-testid="`team-chip-${teamName}`"
      >
        {{ teamName }}
        <ZoneButton
          reset
          class="flex cursor-pointer items-center justify-center rounded-full hover:text-primary-900 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700"
          :aria-label="t('remove', { name: teamName })"
          :data-testid="`team-chip-remove-${teamName}`"
          @click="removeName(teamName)"
        >
          <ZoneIcon
            icon="close"
            class="text-base! leading-none"
            aria-hidden="true"
          />
        </ZoneButton>
      </span>
    </div>
    <p
      v-else
      class="mt-3 text-xs text-gray-400"
    >
      {{ t('empty') }}
    </p>
  </StepCard>
</template>

<script setup>
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { TeamsResource } from '~/resources';
import { SUMMARY_COLUMN } from '~/components/experiments/0239_getting_started_checklist/summaryTable';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZoneTable from 'zone/components/base/Table.vue';
import ZoneTextInput from 'zone/components/base/TextInput.vue';
import { chain, max, required } from '~/zone_inertia/validations';

const TEAM_NAME_MAX_LENGTH = 100;

const { data, completed, organization } = defineProps({
  data: { type: Array, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, failures, finishStep, run } = useStepSave(emit);

const names = ref([]);

const save = () => run(async () => {
  const sent = names.value;
  const results = await Promise.allSettled(sent.map((name) => TeamsResource.create({ id: organization.id }, { team: { name } })));

  names.value = sent.filter((_, index) => results[index].status === 'rejected' || !results[index].value.success);

  const errors = [
    ...failures(results),
    ...results.flatMap(({ value }, index) => (value && !value.success ? [t('team_failed', { name: sent[index], message: value.message })] : [])),
  ];

  return errors.length ? errors : finishStep(organization.id, 'create_teams');
});

const { defineField, errors, meta, handleSubmit, resetForm, validateField } = useForm({
  initialValues: { name: '' },
  validationSchema: {
    name: chain([
      required(),
      max(TEAM_NAME_MAX_LENGTH),
      (value) => !names.value.some((teamName) => teamName.toLowerCase() === value.trim().toLowerCase()) || t('duplicate', { name: value.trim() }),
      (value) => !data.some((team) => team.name.toLowerCase() === value.trim().toLowerCase()) || t('exists', { name: value.trim() }),
    ]),
  },
});
const [name] = defineField('name', ({ errors: fieldErrors }) => ({ validateOnModelUpdate: fieldErrors.length > 0 }));
const validateTypedName = () => name.value && validateField('name');
const summaryColumns = computed(() => [{ key: 'name', label: t('your_teams'), ...SUMMARY_COLUMN }]);
const saveHint = computed(() => (names.value.length === 0 ? t('save_hint') : null));

const addName = handleSubmit((values) => {
  names.value = [...names.value, values.name.trim()];
  resetForm();
});

const removeName = (teamName) => {
  names.value = names.value.filter((candidate) => candidate !== teamName);
  validateTypedName();
};
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Teams keep your account organized. Pick a few to start with.",
    "team_name": "Team name",
    "team_placeholder": "e.g. Marketing",
    "remove": "Remove {name}",
    "empty": "Add a name above to create your first team.",
    "create_teams": "Create teams",
    "add": "Add team",
    "add_hint": "Type a team name first.",
    "save_hint": "Add at least one team name first.",
    "duplicate": "{name} is already in your list.",
    "exists": "A team named {name} already exists.",
    "edit": "Create new team",
    "your_teams": "Your teams",
    "team_failed": "{name}: {message}"
  }
}
</i18n>
